import React, { useState, useEffect } from 'react';
import { Check, Search } from 'lucide-react';

// For Pros who are already registered in Caspio but never set up Stripe payouts.
// They look themselves up (email + ZIP on file), then go straight to Stripe Connect —
// no registration form.

// Survives the Stripe redirect (which reloads the page) so we can re-check status after.
const STORAGE_KEY = 'rcgvpro_existing_connect';

const inputClass = 'w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent';

const postJson = async (path, body) => {
    const resp = await fetch(path, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(body)
    });
    const data = await resp.json().catch(() => ({}));
    if (!resp.ok) {
        const err = new Error(data.error || `Request failed (${resp.status})`);
        err.data = data;
        throw err;
    }
    return data;
};

const ExistingProConnect = () => {
    const [email, setEmail] = useState('');
    const [postalCode, setPostalCode] = useState('');
    const [pro, setPro] = useState(null);       // lookup result
    const [step, setStep] = useState('lookup'); // lookup | confirm | done
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [notice, setNotice] = useState('');
    const [codeCopied, setCodeCopied] = useState(false);

    const lookup = async (creds) => {
        const data = await postJson('/api/existing-pro/lookup', creds);
        setPro(data);
        setStep(data.stripeComplete ? 'done' : 'confirm');
        return data;
    };

    // Returning from Stripe: restore the saved lookup and re-check the account status.
    useEffect(() => {
        const status = new URLSearchParams(window.location.search).get('onboarding');
        if (status !== 'complete' && status !== 'refresh') return;

        let saved = null;
        try { saved = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || 'null'); } catch (_) { /* ignore */ }
        window.history.replaceState(null, '', window.location.pathname);
        if (!saved) return;

        setEmail(saved.email);
        setPostalCode(saved.postalCode);
        setBusy(true);
        lookup(saved)
            .then((data) => {
                if (!data.stripeComplete) {
                    setNotice(status === 'refresh'
                        ? 'Your Stripe link expired. Click below to pick up where you left off.'
                        : "Stripe setup isn't finished yet. Click below to continue where you left off.");
                } else {
                    try { sessionStorage.removeItem(STORAGE_KEY); } catch (_) { /* ignore */ }
                }
            })
            .catch((err) => setError(err.message))
            .finally(() => setBusy(false));
    }, []);

    const handleLookup = async (e) => {
        e.preventDefault();
        setError('');
        setNotice('');
        setBusy(true);
        try {
            await lookup({ email: email.trim(), postalCode: postalCode.trim() });
        } catch (err) {
            setError(err.message);
        } finally {
            setBusy(false);
        }
    };

    const handleConnect = async () => {
        setError('');
        setBusy(true);
        const creds = { email: email.trim(), postalCode: postalCode.trim() };
        try {
            const data = await postJson('/api/existing-pro/connect', creds);
            try { sessionStorage.setItem(STORAGE_KEY, JSON.stringify(creds)); } catch (_) { /* ignore */ }
            window.location.href = data.accountLink;
        } catch (err) {
            if (err.data && err.data.alreadyConnected) {
                await lookup(creds).catch(() => {});
            } else {
                setError(err.message);
            }
            setBusy(false);
        }
    };

    const reset = () => {
        setPro(null);
        setStep('lookup');
        setError('');
        setNotice('');
    };

    const renderLookup = () => (
        <form onSubmit={handleLookup} className="space-y-4">
            <h3 className="text-xl font-semibold text-gray-900">Set Up Your Referral Payouts</h3>
            <p className="text-gray-600">
                Already registered as an RCGV Pro? Look up your registration to connect a bank
                account through Stripe so your referral commissions can be paid out.
            </p>
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email <span className="text-red-500">*</span>
                </label>
                <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                    placeholder="The email you registered with" className={inputClass} />
            </div>
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                    Business ZIP Code <span className="text-red-500">*</span>
                </label>
                <input type="text" inputMode="numeric" required value={postalCode}
                    onChange={(e) => setPostalCode(e.target.value)}
                    placeholder="ZIP code on your registration" className={inputClass} />
            </div>
            <button type="submit" disabled={busy}
                className="flex items-center px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50">
                <Search className="h-4 w-4 mr-2" />
                {busy ? 'Looking up...' : 'Find My Registration'}
            </button>
            <p className="text-sm text-gray-500 pt-2">
                Not registered yet? <a href="/" className="text-blue-600 hover:underline">Start a new registration</a>.
            </p>
        </form>
    );

    const renderConfirm = () => (
        <div className="space-y-4">
            <h3 className="text-xl font-semibold text-gray-900">
                Welcome back{pro.firstName ? `, ${pro.firstName}` : ''}
            </h3>
            <div className="border border-gray-200 rounded-lg divide-y divide-gray-200">
                <div className="flex justify-between px-4 py-2">
                    <span className="text-gray-600">Name</span>
                    <span className="font-medium text-gray-900">{`${pro.firstName} ${pro.lastName}`.trim()}</span>
                </div>
                {pro.firmName && (
                    <div className="flex justify-between px-4 py-2">
                        <span className="text-gray-600">Firm</span>
                        <span className="font-medium text-gray-900">{pro.firmName}</span>
                    </div>
                )}
                <div className="flex justify-between px-4 py-2">
                    <span className="text-gray-600">Email</span>
                    <span className="font-medium text-gray-900">{email.trim()}</span>
                </div>
            </div>
            <div className="bg-blue-50 p-4 rounded-lg">
                <p className="text-sm text-blue-900">
                    {pro.hasStripeAccount
                        ? "You started Stripe setup before but didn't finish. Continue below to complete it."
                        : "To receive referral payments, connect your bank account through Stripe. Your banking information is secured with industry-leading encryption."}
                </p>
            </div>
            <div className="flex justify-between pt-4 border-t border-gray-200">
                <button onClick={reset} className="px-4 py-2 text-blue-600 rounded hover:bg-blue-50">
                    Not you?
                </button>
                <button onClick={handleConnect} disabled={busy}
                    className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50">
                    {busy ? 'Connecting...' : pro.hasStripeAccount ? 'Continue Stripe Setup' : 'Connect with Stripe'}
                </button>
            </div>
        </div>
    );

    const renderDone = () => (
        <div className="space-y-6 text-center">
            <div className="flex justify-center">
                <div className="rounded-full bg-green-100 p-3">
                    <Check className="h-8 w-8 text-green-600" />
                </div>
            </div>
            <h3 className="text-xl font-semibold text-gray-900">Your payouts are set up!</h3>
            <p className="text-gray-600">Your bank account is connected through Stripe.</p>

            {pro.feePercent > 0 && pro.referralCode && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-5 text-left">
                    <h4 className="font-semibold text-blue-900 mb-1">Your referral code</h4>
                    <p className="text-sm text-blue-900 mb-3">
                        Enter this code in the <strong>Product SKU</strong> field on every DocuSign
                        engagement letter you send. It's how your {pro.feePercent}% commission gets
                        tracked and paid out.
                    </p>
                    <div className="flex items-center gap-3">
                        <code className="flex-1 bg-white border border-blue-300 rounded px-3 py-2 font-mono text-lg tracking-wide text-gray-900">
                            {pro.referralCode}
                        </code>
                        <button
                            onClick={() => {
                                navigator.clipboard?.writeText(pro.referralCode)
                                    .then(() => { setCodeCopied(true); setTimeout(() => setCodeCopied(false), 2000); })
                                    .catch(() => {});
                            }}
                            className="px-4 py-2 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 whitespace-nowrap">
                            {codeCopied ? 'Copied!' : 'Copy'}
                        </button>
                    </div>
                </div>
            )}

            <a href="https://www.rcgvaluation.com/rcgv-pro-referral"
                className="inline-block px-6 py-3 bg-blue-600 text-white rounded hover:bg-blue-700">
                Go to RCGV Pro Referral Login
            </a>
        </div>
    );

    return (
        <div className="max-w-2xl mx-auto p-6">
            <div className="mb-8">
                <img src="/RCGVPro_logo.png" alt="RCGVPro Logo" className="h-12 mb-8"
                    onError={(e) => { e.target.style.display = 'none'; }} />
            </div>
            <div className="bg-white rounded-lg shadow-lg p-6">
                {notice && <div className="mb-4 p-3 rounded bg-yellow-50 border border-yellow-200 text-sm text-yellow-900">{notice}</div>}
                {error && <div className="mb-4 p-3 rounded bg-red-50 border border-red-200 text-sm text-red-800">{error}</div>}
                {step === 'lookup' && renderLookup()}
                {step === 'confirm' && pro && renderConfirm()}
                {step === 'done' && pro && renderDone()}
            </div>
        </div>
    );
};

export default ExistingProConnect;
